import { useI18n } from 'vue-i18n';
import type { Luggage, LuggageFromAccounts, LuggageUnit } from '../types';
import { UNITS } from '../config';

import ru from '../config/locales/ru.json';
import kk from '../config/locales/kk.json';
import en from '../config/locales/en.json';

interface ParsedLuggage {
  unit: LuggageUnit;
  value: number;
  count: number;
}

const missingLuggage: ParsedLuggage = {
  unit: 'KG',
  value: 0,
  count: 0,
};

export function parseLuggage(luggage: Luggage): ParsedLuggage {
  const availableBaggage = luggage.free?.ADT || luggage.free?.YTH;

  if (!availableBaggage || availableBaggage.value === 0) {
    return missingLuggage;
  }

  if (availableBaggage.unit === UNITS.PC) {
    return {
      unit: availableBaggage.unit,
      value: 'pc' in availableBaggage ? availableBaggage.pc?.value ?? 0 : 0,
      count: availableBaggage.value,
    };
  }

  if (availableBaggage.unit === UNITS.KG) {
    return {
      unit: availableBaggage.unit,
      value: availableBaggage.value,
      count: 1,
    };
  }

  return missingLuggage;
}

export function luggageToStringFromAccounts(
  luggage: LuggageFromAccounts,
): string {
  const { t } = useI18n({ messages: { ru, kk, en } });

  if (luggage.value < 1) {
    return '';
  }

  if (luggage.unit === UNITS.PC && luggage.pc) {
    if (luggage.value > 1) {
      return `${luggage.value} x ${luggage.pc.value} ${t(
        `luggage.unit_${luggage.pc.unit}`,
      )}`;
    }

    return `${luggage.pc.value} ${t(`luggage.unit_${luggage.pc.unit}`)}`;
  }

  const factor = luggage.factor > 1 ? `${luggage.factor} x` : '';
  const translatedUnit = t(`luggage.unit_${luggage.unit}`);

  return [factor, luggage.value, translatedUnit].join(' ');
}

export function luggageToString({ luggage }: { luggage: Luggage }): string {
  const { t } = useI18n({ messages: { ru, kk, en } });
  const { count, value, unit } = parseLuggage(luggage);

  if (count <= 0) {
    return '';
  }

  const isKG = value > 0;
  const unitType = isKG ? UNITS.KG : UNITS.PC;
  const translatedUnit = t(`luggage.unit_${unitType}`);
  const shouldDisplayCount = unit === UNITS.PC || count > 1;

  if (isKG) {
    return shouldDisplayCount
      ? `${count} x ${value} ${translatedUnit}`
      : `${value} ${translatedUnit}`;
  }

  return shouldDisplayCount ? `${count} ${translatedUnit}` : translatedUnit;
}
